import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, Fuel, Gauge, Cog, Calendar } from "lucide-react";
import LegalLayout from "../components/LegalLayout";
import DemoBanner from "../components/DemoBanner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const PAGE_SIZE = 24;

/**
 * Public car showcase for private customers. Routed at `/privati/auto`.
 *
 * Powered by the fail-closed public endpoint /api/public/showcase-vehicles
 * so no dealer-internal signal is ever exposed to the browser. Cards CTA
 * deep-links to /privati?car=<label>&vid=<id> to prefill the lead form.
 */
export default function PrivatiAutoPage() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState(null);
  const [filters, setFilters] = useState({ brand: "all", fuel: "all", year_min: "all" });
  const [brands, setBrands] = useState([]);

  const params = useMemo(() => {
    const q = new URLSearchParams();
    q.set("limit", String(PAGE_SIZE));
    if (filters.brand && filters.brand !== "all") q.set("brand", filters.brand);
    if (filters.fuel && filters.fuel !== "all") q.set("fuel", filters.fuel);
    if (filters.year_min && filters.year_min !== "all") q.set("year_min", filters.year_min);
    return q;
  }, [filters]);

  const fetchPage = async (nextOffset = 0, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setErr(null);
    try {
      const q = new URLSearchParams(params);
      q.set("offset", String(nextOffset));
      const { data } = await api.get(`/public/showcase-vehicles?${q.toString()}`);
      setTotal(data.total || 0);
      setItems((prev) => (append ? [...prev, ...(data.items || [])] : data.items || []));
      setOffset(nextOffset);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchPage(0, false);
    // depends on serialized params to avoid re-fetch on unrelated re-renders
  }, [params.toString()]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Fetch a lightweight brand list from the same endpoint (first page)
    (async () => {
      try {
        const { data } = await api.get(`/public/showcase-vehicles?limit=60&offset=0`);
        const uniq = Array.from(new Set((data.items || []).map((v) => v.brand).filter(Boolean))).sort();
        setBrands(uniq);
      } catch {
        setBrands([]);
      }
    })();
  }, []);

  const hasMore = items.length < total;
  const fuels = ["benzina", "diesel", "elettrica", "ibrida", "gpl", "metano"];
  const yearOptions = ["2024", "2023", "2022", "2021", "2020", "2018"];
  const hasActiveFilters =
    (filters.brand && filters.brand !== "all") ||
    (filters.fuel && filters.fuel !== "all") ||
    (filters.year_min && filters.year_min !== "all");
  const catalogEmpty = total === 0 && !hasActiveFilters && !loading;

  return (
    <LegalLayout
      testid="privati-auto-page"
      overline={t("privati_auto.overline")}
      title={t("privati_auto.title")}
    >
      <div className="-mt-2 mb-6">
        <DemoBanner testid="privati-auto-demo-banner" />
      </div>
      <p
        data-testid="privati-auto-intro"
        className="text-slate-600 -mt-4 mb-6 max-w-2xl leading-relaxed"
      >
        {t("privati_auto.intro")}
      </p>
      <p className="text-[13px] text-slate-500 mb-8 max-w-2xl">{t("privati.form_note")}</p>

      {/* Filters — hidden when the catalog is empty (nothing to filter yet). */}
      {!catalogEmpty && (
        <div className="mb-8 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="privati-auto-filters">
        <FilterSelect
          testid="filter-brand"
          label={t("privati_auto.filter_brand")}
          value={filters.brand}
          onChange={(v) => setFilters((s) => ({ ...s, brand: v }))}
          options={[{ v: "all", l: t("privati_auto.filter_all") }, ...brands.map((b) => ({ v: b, l: b }))]}
        />
        <FilterSelect
          testid="filter-fuel"
          label={t("privati_auto.filter_fuel")}
          value={filters.fuel}
          onChange={(v) => setFilters((s) => ({ ...s, fuel: v }))}
          options={[
            { v: "all", l: t("privati_auto.filter_all") },
            ...fuels.map((f) => ({ v: f, l: t(`fuel.${f}`, { defaultValue: f }) })),
          ]}
        />
        <FilterSelect
          testid="filter-year"
          label={t("privati_auto.filter_year_min")}
          value={filters.year_min}
          onChange={(v) => setFilters((s) => ({ ...s, year_min: v }))}
          options={[{ v: "all", l: t("privati_auto.filter_all") }, ...yearOptions.map((y) => ({ v: y, l: `≥ ${y}` }))]}
        />
      </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-20" data-testid="privati-auto-loading">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : err ? (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">
          {err}
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="privati-auto-empty"
          className="rounded-2xl border border-slate-100 bg-slate-50 px-6 py-14 text-center"
        >
          <p className="text-slate-700 font-semibold text-lg leading-snug max-w-lg mx-auto">
            {catalogEmpty ? t("privati_auto.empty_catalog_title") : t("privati_auto.empty_title")}
          </p>
          {!catalogEmpty && (
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              {t("privati_auto.empty_body")}
            </p>
          )}
          <Button asChild className="mt-6 h-11 rounded-full bg-blue-600 hover:bg-blue-700">
            <Link
              to="/privati"
              data-testid="privati-auto-empty-cta"
            >
              {catalogEmpty ? t("privati_auto.find_car_cta") : t("privati_auto.request_generic_cta")}
            </Link>
          </Button>
        </div>
      ) : (
        <>
          <div
            data-testid="privati-auto-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {items.map((v) => (
              <ShowcaseCard key={v.id} v={v} t={t} lang={i18n.language} />
            ))}
          </div>

          {hasMore && (
            <div className="mt-10 flex justify-center">
              <Button
                onClick={() => fetchPage(offset + PAGE_SIZE, true)}
                disabled={loadingMore}
                data-testid="privati-auto-load-more"
                variant="outline"
                className="h-11 rounded-full border-slate-200 px-6"
              >
                {loadingMore ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    {t("privati_auto.load_more")}
                    <span className="ml-2 text-xs text-slate-400">
                      · {items.length}/{total}
                    </span>
                  </>
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </LegalLayout>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
        {label}
      </label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="h-11 rounded-xl">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.v} value={o.v}>
              {o.l}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function formatEur(n, lang) {
  const locale = lang === "de" ? "de-DE" : lang === "en" ? "en-GB" : "it-IT";
  try {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n);
  } catch {
    return String(n);
  }
}

function formatKm(km, lang) {
  const locale = lang === "de" ? "de-DE" : lang === "en" ? "en-GB" : "it-IT";
  try {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(km);
  } catch {
    return String(km);
  }
}

function ShowcaseCard({ v, t, lang }) {
  const label = [v.brand, v.model, v.trim, v.year].filter(Boolean).join(" ");
  const detailUrl = `/privati?car=${encodeURIComponent(label)}&vid=${encodeURIComponent(v.id)}`;
  const photo = (v.images && v.images[0]) || null;
  const flag = v.country === "DE" ? "🇩🇪" : v.country === "IT" ? "🇮🇹" : "";
  return (
    <article
      data-testid={`showcase-card-${v.id}`}
      className="group rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
    >
      <Link to={detailUrl} className="block">
        <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
          {photo ? (
            <img
              src={photo}
              alt={label}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            />
          ) : null}
          <div className="absolute top-3 left-3 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[10px] font-bold tracking-wider text-slate-700 uppercase border border-slate-200">
            {flag} {v.country}
          </div>
        </div>
      </Link>
      <div className="p-5">
        <h3 className="font-heading font-semibold text-[17px] tracking-tight text-slate-950 line-clamp-1">
          {v.brand} {v.model}
        </h3>
        {v.trim && <p className="text-[12px] text-slate-500 line-clamp-1">{v.trim}</p>}

        <div className="mt-4 grid grid-cols-2 gap-y-2 gap-x-3 text-[12px] text-slate-600">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-slate-400" /> {v.year}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Gauge className="w-3.5 h-3.5 text-slate-400" /> {formatKm(v.km, lang)} km
          </span>
          <span className="inline-flex items-center gap-1.5 capitalize">
            <Fuel className="w-3.5 h-3.5 text-slate-400" /> {t(`fuel.${v.fuel}`, { defaultValue: v.fuel })}
          </span>
          <span className="inline-flex items-center gap-1.5 capitalize">
            <Cog className="w-3.5 h-3.5 text-slate-400" /> {t(`transmission.${v.transmission}`, { defaultValue: v.transmission })}
          </span>
        </div>

        <div className="mt-5 pt-4 border-t border-slate-100 flex items-end justify-between gap-3">
          <div>
            {v.retail_from ? (
              <>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                  {t("privati_auto.retail_from")}
                </p>
                <p className="text-[17px] font-heading font-bold text-slate-950 tracking-tight">
                  {formatEur(v.retail_from, lang)} €
                </p>
                <p className="text-[10px] text-slate-400 leading-tight">
                  {t("privati_auto.retail_note")}
                </p>
              </>
            ) : (
              <p className="text-[15px] font-heading font-semibold text-slate-700 tracking-tight">
                {t("privati_auto.request_quote")}
              </p>
            )}
          </div>
          <Button asChild className="h-10 rounded-full bg-slate-950 hover:bg-black text-white font-semibold px-4 text-[12px] shadow-md flex-shrink-0">
            <Link to={detailUrl} data-testid={`showcase-card-${v.id}-cta`}>
              {t("privati_auto.card_cta")}
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </article>
  );
}
